<template>
  <div class="reporting-section">
    <div class="section-header">
      <BoostingPageAvatars :pages="pages" size="large" />
      <h2 v-if="global">
        {{ $t('dash.boosting.global') }}
        <span class="section-sub">· {{ $t('dash.boosting.global_pages', { count: pages.length }) }}</span>
      </h2>
      <h2 v-else>{{ pages[0].name }}</h2>
    </div>

    <div class="activate-banner" v-for="page in disabledPages" :key="'activate-' + page.id">
      <BoostingPageAvatars :pages="[page]" />
      <span class="activate-text">{{ $t('dash.boosting.activate.title', { name: page.name }) }}</span>
      <button class="activate-btn" :disabled="!!activating[page.id]" @click="activate(page)">
        <i v-if="activating[page.id]" class="material-icons spin">hourglass_empty</i>
        <span v-else class="boosting-icon"></span>
        {{ $t('dash.boosting.activate.button') }}
      </button>
    </div>

    <div class="post-empty" v-if="loading">
      <i class="material-icons spin">hourglass_empty</i>
      <p>{{ $t('dash.boosting.loading') }}</p>
    </div>

    <div class="post-empty" v-else-if="error">
      <i class="material-icons" style="color:#e0d0ea">error_outline</i>
      <p style="color:#c0392b">{{ error }}</p>
      <button class="page-btn" style="width:auto;padding:0 14px" @click="load">{{ $t('dash.reporting.retry') }}</button>
    </div>

    <div class="post-empty" v-else-if="!hasData">
      <span class="boosting-icon"></span>
      <p>{{ $t('dash.boosting.empty') }}</p>
    </div>

    <template v-else>
      <div class="metric-row">
        <MetricCard
          :value="totals.invited"
          :previous-value="previous ? previous.invited : null"
          :label="$t('dash.boosting.invited')"
          icon="send"
        />
        <MetricCard
          :value="totals.subscribed"
          :previous-value="previous ? previous.subscribed : null"
          :label="$t('dash.boosting.subscribed')"
          icon="person_add"
        />
        <Tooltip :text="$t('dash.boosting.tooltip.conversion_rate')" position="top">
          <MetricCard
            :value="totals.conversion_rate"
            :previous-value="previous ? previous.conversion_rate : null"
            :label="$t('dash.boosting.conversion_rate')"
            icon="show_chart"
            format="percent"
          />
        </Tooltip>
        <Tooltip :text="$t('dash.boosting.tooltip.pending')" position="top">
          <MetricCard
            :value="totals.pending"
            :label="$t('dash.boosting.pending')"
            icon="hourglass_empty"
          />
        </Tooltip>
      </div>

      <template v-if="graphData.length && graphLabels.length">
        <div class="graph-title">{{ $t('dash.boosting.graph') }}</div>
        <div class="graph-wrap">
          <Graph
            :id="`boosting-${pageIds.join('-')}`"
            :data="graphData"
            :labels="graphLabels"
            :stacked="false"
            height="220px"
            width="100%"
          />
        </div>
      </template>

      <template v-if="recentSubscribers.length">
        <div class="subsection-header">
          <i class="material-icons subsection-icon">group</i>
          <span>{{ $t('dash.boosting.recent_subscribers') }}</span>
        </div>
        <div class="subscriber-list">
          <div class="subscriber-row" v-for="sub in recentSubscribers" :key="`${sub.page_id}-${sub.user_id}`">
            <span class="subscriber-avatar">{{ (sub.user_name || '?')[0].toUpperCase() }}</span>
            <BoostingPageAvatars v-if="global" :pages="pagesFor([sub.page_id])" />
            <a
              v-if="sub.user_url"
              class="subscriber-name"
              :href="sub.user_url"
              target="_blank"
              rel="noopener noreferrer"
            >{{ sub.user_name || sub.user_id }}</a>
            <span v-else class="subscriber-name">{{ sub.user_name || sub.user_id }}</span>
            <span class="subscriber-date">{{ formatDate(sub.subscribed_at) }}</span>
            <a
              v-if="sub.post_id"
              class="subscriber-post"
              :href="`https://www.facebook.com/${sub.post_id}`"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('dash.boosting.view_post') }}
              <i class="material-icons">open_in_new</i>
            </a>
          </div>
        </div>
      </template>

      <BoostingInvitations :key="invitationsKey" :pages="pages" :global="global" @changed="load(true)" />
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import MetricCard from '../reporting-web/panels/MetricCard.vue'
import Graph from '../reporting-web/Graph.vue'
import Tooltip from '../tooltip/tooltipInit.vue'
import BoostingInvitations from './BoostingInvitations.vue'
import BoostingPageAvatars from './BoostingPageAvatars.vue'
import { EventBus } from '@/event-bus'
import { trackDiscovery } from './boostingTasks'

export default {
  name: 'BoostingPage',
  components: { MetricCard, Graph, Tooltip, BoostingInvitations, BoostingPageAvatars },
  props: {
    pages:     { type: Array, required: true },
    global:    { type: Boolean, default: false },
    dateStart: { type: String, required: true },
    dateEnd:   { type: String, required: true },
    prevRange: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      error: null,
      boosting: null,
      activating: {},
      invitationsKey: 0,
    }
  },
  computed: {
    pageIds() { return this.pages.map(p => p.id) },
    disabledPages() {
      if (!this.boosting) return []
      var enabled = this.boosting.enabled_pages || []
      return this.pages.filter(p => !enabled.includes(p.id))
    },
    hasData() { return !!(this.boosting && this.boosting.has_data) },
    totals() { return (this.boosting && this.boosting.totals) || {} },
    previous() { return (this.boosting && this.boosting.previous) || null },
    recentSubscribers() { return (this.boosting && this.boosting.recent_subscribers) || [] },
    dateFormat() {
      return this.$i18n.locale === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
    },
    graphPoints() { return (this.boosting && this.boosting.graph) || [] },
    graphLabels() {
      return this.graphPoints.map(d => moment(d.date * 1000).format(this.dateFormat))
    },
    graphData() {
      if (!this.graphPoints.length) return []
      return [
        {
          label: this.$t('dash.boosting.invited_series'),
          data: this.graphPoints.map(d => d.invited || 0),
          type: 'bar',
        },
        {
          label: this.$t('dash.boosting.subscribed_series'),
          data: this.graphPoints.map(d => d.subscribed || 0),
          type: 'bar',
          color: '#10B981',
        },
      ]
    },
  },
  mounted() {
    EventBus.$on('boostingDiscoveryProgress', this.onDiscoveryProgress)
    this.load()
  },
  beforeDestroy() {
    EventBus.$off('boostingDiscoveryProgress', this.onDiscoveryProgress)
  },
  methods: {
    async load(silent) {
      if (silent !== true) this.loading = true
      this.error = null
      try {
        var apiHost = process.env.VUE_APP_API_HOST
        var since = moment(this.dateStart).unix()
        var until = moment(this.dateEnd).endOf('day').unix()
        var tz = new Date().getTimezoneOffset()
        var url = apiHost + '/reporting/boosting?page_ids=' + this.pageIds.join(',') + '&since=' + since + '&until=' + until + '&timezone=' + tz
        if (this.prevRange) url += '&prev_since=' + this.prevRange.since + '&prev_until=' + this.prevRange.until
        var token = localStorage.getItem('token')
        var res = await fetch(url, { headers: { Authorization: token ? ('Bearer ' + token) : '' } })
        var json = await res.json()
        if (!res.ok) throw new Error(json.message || json.error || 'Erreur serveur')
        this.boosting = json.boosting || null
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    onDiscoveryProgress({ page_id }) {
      if (this.pageIds.includes(page_id)) this.load(true)
    },
    async activate(page) {
      this.$set(this.activating, page.id, true)
      try {
        var token = localStorage.getItem('token')
        var res = await fetch(process.env.VUE_APP_API_HOST + '/reporting/boosting/enable', {
          method: 'POST',
          headers: { Authorization: token ? ('Bearer ' + token) : '', 'Content-Type': 'application/json' },
          body: JSON.stringify({ page_id: page.id }),
        })
        var json = await res.json().catch(() => ({}))
        if (!res.ok) {
          var key = json.error
          this.$vs.notify({ title: this.$t('dash.boosting.invitations.error_title'), text: key && this.$te(key) ? this.$t(key) : this.$t('dash.boosting.invitations.error_generic'), color: 'danger', position: 'top-right' })
          return
        }
        this.$vs.notify({ title: this.$t('dash.tab.boosting'), text: this.$t('dash.boosting.activate.success', { name: page.name }), color: 'success', position: 'top-right' })
        trackDiscovery(page, (key, params) => this.$i18n.t(key, params))
        await this.load(true)
        this.invitationsKey++
      } finally {
        this.$delete(this.activating, page.id)
      }
    },
    pagesFor(ids) {
      return this.pages.filter(p => ids.includes(p.id))
    },
    formatDate(date) {
      return date ? moment(date).format(this.dateFormat) : ''
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }
.activate-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #f9f7fb;
  border: 1px solid #f1edf5;
  border-radius: 5.6px;
  font-size: 13px;
  color: #555E75;
}
.activate-text { flex: 1; min-width: 0; }
.activate-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 5px 12px;
  border-radius: 8px;
  border: 1.5px solid #6B1F4D;
  background: #6B1F4D;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}
.activate-btn:hover:not(:disabled) { opacity: 0.88; }
.activate-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.activate-btn .material-icons, .activate-btn .boosting-icon { font-size: 15px; }
.boosting-icon {
  display: inline-block;
  width: 1em;
  height: 1em;
  flex-shrink: 0;
  background-color: currentColor;
  -webkit-mask: url('../../../assets/img/tabs/ico-boosting.svg') center / contain no-repeat;
  mask: url('../../../assets/img/tabs/ico-boosting.svg') center / contain no-repeat;
}
.post-empty .boosting-icon { font-size: 40px; color: #e0d0ea; }
.section-sub { font-size: 13px; font-weight: 500; color: #9ba3b7; }
.metric-row { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 20px; }
.metric-row > .hasTooltip {
  flex: 1;
  min-width: 160px;
  display: flex;
}
.graph-title {
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 16px 0 6px;
}
.graph-wrap { width: 100%; margin-bottom: 8px; }
.subsection-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 24px 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: #555E75;
}
.subsection-icon { font-size: 18px; color: #6B1F4D; }
.subscriber-list { display: flex; flex-direction: column; }
.subscriber-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 4px;
  font-size: 13px;
  color: #121826;
  border-bottom: 1px solid #f1edf5;
}
.subscriber-row:last-child { border-bottom: none; }
.subscriber-avatar {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #f1edf5;
  color: #6B1F4D;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}
.subscriber-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  color: #121826;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
}
a.subscriber-name:hover { color: #6B1F4D; }
.subscriber-date { color: #555E75; font-size: 12px; flex-shrink: 0; }
.subscriber-post {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  font-size: 12px;
  color: #6B1F4D;
  text-decoration: none;
}
.subscriber-post:hover { text-decoration: underline; }
.subscriber-post .material-icons { font-size: 14px; }
.post-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;
  color: #9ba3b7;
}
.post-empty .material-icons { font-size: 40px; color: #e0d0ea; }
.post-empty p { font-size: 14px; }
.page-btn {
  display: flex; align-items: center; justify-content: center;
  height: 30px; border-radius: 8px;
  border: 1px solid #e0d0ea; background: #fff; color: #6B1F4D;
  cursor: pointer; transition: all 0.15s;
}
.page-btn:hover { background: #f1edf5; }
.spin { animation: spin 2s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
